import Grid from "@mui/material/Grid2";
import {
  Button,
  Dialog,
  DialogTitle,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { Stack } from "@mui/system";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../features/userSlice";
import SaveIcon from "@mui/icons-material/Save";
import { useDispatch } from "react-redux";
import { CalculationTypesEndPointLogic } from "../../../../../logic/DataCollection/Unifier/CalculationTypesEndpointLogic";
import {
  validateDescriptionField,
  validateTextField,
} from "../../../../../utils/helpers";

interface EditCalculationProps {
  open: boolean;
  handleClose: () => void;
}

const EditCalculationType = ({ open, handleClose }: EditCalculationProps) => {
  const { selectedRow } = useSelector(
    (state: any) => state.unifierCalculationTypes
  );
  const [loading, setLoading] = useState(false);
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const dispatch = useDispatch();
  const [initialName, setInitialName] = useState("");
  const [initialCustomExpression, setInitialCustomExpression] = useState("");
  const [name, setName] = useState("");
  const [customExpression, setCustomExpression] = useState("");
  const [id, setId] = useState("");
  const [error, setError] = useState({
    name: "",
    customExpression: "",
  });

  //-- Function to close the modal and reset the form
  const closeEditCollectionModal = () => {
    setName("");
    setCustomExpression("");
    setId("");
    setError({ name: "", customExpression: "" });
    handleClose();
  };

  useEffect(() => {
    if (selectedRow) {
      setName(selectedRow.name);
      setCustomExpression(selectedRow.custom_expression);
      setId(selectedRow.id);
      setInitialName(selectedRow.name);
      setInitialCustomExpression(selectedRow.custom_expression);
    }
  }, [selectedRow]);

  //-- Function to check if the form is valid
  const isFormValid = () => {
    return (
      name?.trim() !== "" &&
      customExpression?.trim() !== "" &&
      !error.name &&
      !error.customExpression
    );
  };
  //-- Function to update calculation type
  const handleUpdateCalculationType = async () => {
    if (!isFormValid()) return;
    // Check if the name and custom expression have not changed
    if (name === initialName && customExpression === initialCustomExpression) {
      closeEditCollectionModal();
      return;
    }
    const data = { name, custom_expression: customExpression };
    setLoading(true);
    try {
      const response = await CalculationTypesEndPointLogic.editCollectionTypes(
        oauthTokenHeader,
        data,
        id
      );

      if (!response || !response.status) {
        throw new Error("Failed to update calculation type.");
      } else if (response.status === 200) {
        await CalculationTypesEndPointLogic.getCollectionTypes(
          oauthTokenHeader,
          dispatch
        );
        closeEditCollectionModal();
        dispatch(
          setDisplayErrorSnackBar({
            message: "Calculation type has been updated successfully.",
            type: "success",
          })
        );
      }
    } catch (error) {
      const errorMessage =
        typeof error === "string"
          ? error
          : error instanceof Error
          ? error.message
          : "An unexpected error occurred.";

      dispatch(
        setDisplayErrorSnackBar({ message: errorMessage, type: "error" })
      );

      return; // Stop execution, do not close modal
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <Dialog
        open={open}
        onClose={closeEditCollectionModal}
        PaperProps={{ sx: { width: "900px", maxWidth: "900px" } }}
      >
        <Paper sx={{ padding: "16px 24px 20px 24px" }}>
          <DialogTitle sx={{ padding: "0px 0px 16px 0px" }}>
            Edit Type
          </DialogTitle>
          <Grid container mt={0} columnSpacing={3}>
            <Grid size={6}>
              <TextField
                value={name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setName(e.target.value);
                  let errorMsg = validateTextField(e.target.value);
                  setError((prev) => ({
                    ...prev,
                    name: errorMsg,
                  }));
                }}
                error={!!error.name}
                helperText={error.name}
                required
                label="Name" // Set your label here
                placeholder="How to name this type" // Set your placeholder here
                color="secondary"
                size="medium"
                fullWidth
              />
            </Grid>
            <Grid size={12} mt={2}>
              <TextField
                value={customExpression}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setCustomExpression(e.target.value);
                  let errorMsg = validateDescriptionField(e.target.value);
                  setError((prev) => ({
                    ...prev,
                    customExpression: errorMsg,
                  }));
                }}
                error={!!error.customExpression}
                helperText={error.customExpression}
                required
                label="Description" // Set your label here
                placeholder="What does this type do" // Set your placeholder here
                color="secondary"
                size="medium"
                fullWidth
              />
            </Grid>
          </Grid>
          <Typography
            sx={{ fontSize: "12px", marginTop: "8px", color: "#00000099" }}
          >
            All fields are mandatory.
          </Typography>

          <Stack flexDirection={"row"} justifyContent={"end"} marginTop={2}>
            <Button
              color="secondary"
              onClick={handleClose}
              sx={{ mr: 1 }}
              data-tracker="380"
            >
              Cancel
            </Button>
            <Button
              data-tracker="379"
              variant="contained"
              color="secondary"
              loading={loading}
              loadingPosition="end"
              endIcon={loading && <SaveIcon />}
              disabled={!isFormValid()}
              onClick={handleUpdateCalculationType}
            >
              save changes <SaveOutlinedIcon sx={{ ml: 1 }} />
            </Button>
          </Stack>
        </Paper>
      </Dialog>
    </>
  );
};

export default EditCalculationType;
